/* ==========================================================================
   BELORA — SKIMS PURE · header behaviour
   Measured on live skims.com 2026-09-10 — see docs/skims/03-HEADER-NAV-SEARCH.md

   Two scroll behaviours, both pinned by binary search rather than estimated:

   1. DESKTOP (>=1024): the nav row is transparent over the hero and turns
      solid white once scrollY > 150. Measured: transparent at 150,
      rgb(255,255,255) at 151.

   2. MOBILE (<1024): the 52px search row collapses into an inline pill once
      scrollY passes 150. Re-measured frame by frame 13 Sep 2026 (gs-skhdr,
      gs-anchor): the reference does NOT scrub. Crossing 150 starts a 300ms
      cubic-bezier(.4,0,.2,1) transition (146 -> 94 in ~280ms) and the browser's
      scroll anchoring takes the 52px back out of scrollY while it runs, so the
      page under the header never moves by itself: the heading steps exactly
      -20 for every 20px wheel step. The "151->146, 152->144, 155->138, 170->123,
      200->94" pairs this file used to scrub between were anchoring caught in
      the middle of that transition.
      Our scrub needed anchoring switched off (sk-header.css), and with it off
      the page moved at twice the finger's speed for those 52px (heading steps
      -29 / -40 / -40). Where the browser anchors, the reference's model now
      runs. The reference also jitters when a scroll comes to rest just past
      150 (anchoring pulls scrollY back under the threshold, the row re-opens,
      and it repeats — scrollY 148 <-> 157 for as long as it was sampled); a
      hysteresis band closes that loop: collapse above 150, re-open below 94.
      Engines without working scroll anchoring keep the scrub (Apple WebKit
      included, measured below): a toggle there moves the page on its own.
   ========================================================================== */

(function () {
  'use strict';

  var SOLID_AT = 150;        // desktop: nav row turns solid above this
  var COLLAPSE_START = 151;  // mobile: the collapse begins here
  var COLLAPSE_RANGE = 52;   // mobile: total collapse distance
  var COLLAPSE_AFTER = 150;  // anchored toggle: collapsed once scrollY passes this (reference: 150 open, 152 closing)
  var EXPAND_BELOW = 94;     // anchored toggle: re-opens only under this — 150 - 52, less 4 of margin
  // The toggle is only right where anchoring really holds the page still. WebKit answers
  // CSS.supports('overflow-anchor') with true yet did not hold it: with the toggle the heading jumped
  // 60px then 32px on the way down (gs-anchor webkit, 13 Sep 2026). Apple's engine — every iPhone and
  // iPad browser, and Safari — therefore keeps the scrub, which is what it had before.
  var ua = navigator.userAgent || '';
  var appleWebKit = /iP(hone|ad|od)/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    (/AppleWebKit/.test(ua) && !/(Chrome|Chromium|Edg|OPR|Android|SamsungBrowser)/.test(ua));
  var ANCHORS = !appleWebKit && !!(window.CSS && CSS.supports && CSS.supports('overflow-anchor', 'auto'));
  if (ANCHORS) document.documentElement.classList.add('sk-anchor');

  // Measured collapsed geometry of the search row (SKIMS, 390 / 768 / 320):
  //   expanded  top 60  left 16   right 16
  //   collapsed top  8  left 128  right 144
  // left:128 is fixed at every width — the reference keeps a 108px logo slot
  // (16 + 108 + 4). Deriving it from our narrower wordmark put the pill 40px
  // further left and 4px from the letters; the slot is the system, not the mark.
  var TOP_FROM = 60, TOP_TO = 8;
  var SIDE_FROM = 16, LEFT_TO = 128, RIGHT_TO = 144;

  var header = document.querySelector('[data-sk-header]');
  if (!header) return;

  var searchRow = header.querySelector('[data-sk-search-row]');
  var navRow = header.querySelector('.sk-navrow');
  var desktopQuery = window.matchMedia('(min-width: 1024px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ticking = false;
  var lastSolid = null;
  var lastScrub = null;

  function apply() {
    ticking = false;
    var y = window.scrollY || window.pageYOffset || 0;

    var solid = y > SOLID_AT;
    if (solid !== lastSolid) {
      header.classList.toggle('is-scrolled', solid);
      lastSolid = solid;
    }

    if (!searchRow || !navRow) return;

    if (desktopQuery.matches) {
      if (lastScrub !== null) {
        searchRow.style.removeProperty('--sk-search-top');
        searchRow.style.removeProperty('--sk-search-left');
        searchRow.style.removeProperty('--sk-search-right');
        navRow.style.removeProperty('height');
        header.classList.remove('is-collapsed');
        lastScrub = null;
      }
      return;
    }

    if (ANCHORS) {
      // The reference's model: past 150 the row collapses on its own 300ms transition and scroll
      // anchoring absorbs it. It re-opens only under EXPAND_BELOW; otherwise anchoring's own pull-back
      // (up to 52px) would drop scrollY under 150, re-open the row and loop.
      var wasShut = lastScrub === COLLAPSE_RANGE;
      var shut = lastScrub === null ? y > COLLAPSE_AFTER : (wasShut ? y >= EXPAND_BELOW : y > COLLAPSE_AFTER);
      var target = shut ? COLLAPSE_RANGE : 0;
      if (target === lastScrub) return;
      var landing = lastScrub === null;     // load, resize, breakpoint change: land there, do not animate
      lastScrub = target;
      paintRow(shut ? 1 : 0, landing ? 'none' : '');
      if (landing) {
        void navRow.offsetHeight;           // commit the landed values before handing transitions back
        searchRow.style.transition = '';
        navRow.style.transition = '';
      }
      return;
    }

    var scrubbed = Math.min(Math.max(y - COLLAPSE_START, 0), COLLAPSE_RANGE);
    if (scrubbed === lastScrub) return;
    lastScrub = scrubbed;
    // A scroll-linked scrub must not also run a CSS transition, or the two
    // fight and the header lags behind the finger.
    paintRow(scrubbed / COLLAPSE_RANGE, 'none');
  }

  function paintRow(t, transition) {
    searchRow.style.transition = transition;   // '' hands the transition back to the stylesheet
    navRow.style.transition = transition;
    searchRow.style.setProperty('--sk-search-top', (TOP_FROM - (TOP_FROM - TOP_TO) * t) + 'px');
    searchRow.style.setProperty('--sk-search-left', (SIDE_FROM + (LEFT_TO - SIDE_FROM) * t) + 'px');
    searchRow.style.setProperty('--sk-search-right', (SIDE_FROM + (RIGHT_TO - SIDE_FROM) * t) + 'px');
    navRow.style.height = (109 - COLLAPSE_RANGE * t) + 'px';
    header.classList.toggle('is-collapsed', t >= 1);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }

  function reset() { lastScrub = null; lastSolid = null; apply(); }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', reset, { passive: true });
  desktopQuery.addEventListener('change', reset);
  if (document.readyState === 'complete') reset();
  else window.addEventListener('load', reset);
  reset();

  /* ------------------------------------------------------------------------
     Promo carousel — rotating slides with a real pause control.
     SKIMS ships `button[aria-label="Pause promo banner"]`; an auto-rotating
     banner without one is an accessibility failure, so this is not optional.
     ------------------------------------------------------------------------ */

  var promo = header.querySelector('[data-sk-promo]');
  if (promo) {
    var slides = Array.prototype.slice.call(promo.querySelectorAll('[data-sk-slide]'));
    var pauseBtn = promo.querySelector('[data-sk-promo-pause]');
    var interval = parseInt(promo.getAttribute('data-interval'), 10) || 5000;
    var index = 0;
    var timer = null;
    var paused = false;

    function show(i) {
      slides.forEach(function (s, n) {
        var active = n === i;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    // The next message still worth showing: a countdown that has run out
    // leaves the rotation (data-expired) and is never shown again.
    function nextIndex() {
      for (var k = 1; k <= slides.length; k++) {
        var j = (index + k) % slides.length;
        if (!slides[j].hasAttribute('data-expired')) return j;
      }
      return index;
    }
    function live() { return slides.filter(function (s) { return !s.hasAttribute('data-expired'); }).length; }

    function start() {
      if (timer || live() < 2 || paused || reduceMotion.matches) return;
      timer = window.setInterval(function () {
        index = nextIndex();
        show(index);
      }, interval);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    if (slides.length) show(0);
    // Arm the fade only once the first message is painted, or the page would
    // open on a 300ms-delayed fade-in.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { promo.classList.add('is-armed'); });
    });

    // One place writes the control's state: glyph (via data-state) and
    // accessible name move together, in the page's own language. The label
    // used to flip to hard-coded English on the first press, and the glyph
    // never changed at all.
    function paint() {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('data-state', paused ? 'paused' : 'playing');
      var l = pauseBtn.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
      if (l) pauseBtn.setAttribute('aria-label', l);
    }

    // Pressing play is an explicit request: it rotates even under reduced
    // motion (the fade is switched off there in CSS) and even while the button
    // itself still holds focus.
    function forceStart() {
      if (timer || live() < 2) return;
      timer = window.setInterval(function () {
        index = nextIndex();
        show(index);
      }, interval);
    }

    /* Countdown messages tick once a second from the merchant's end time.
       At zero the message leaves the rotation for good; if it was on screen
       the next live message takes its place. A cached page that arrives with
       an already-past end time drops it before it is ever shown. */
    var cds = slides.filter(function (s) { return s.hasAttribute('data-sk-countdown'); });
    function tick() {
      var now = Math.floor(Date.now() / 1000);
      cds.forEach(function (s) {
        if (s.hasAttribute('data-expired')) return;
        var left = parseInt(s.getAttribute('data-ends'), 10) - now;
        if (!(left > 0)) {
          s.setAttribute('data-expired', '');
          s.hidden = true;
          if (s.classList.contains('is-active')) { index = nextIndex(); show(index); }
          if (live() < 2) { stop(); if (pauseBtn) pauseBtn.hidden = true; }
          return;
        }
        var u = (s.getAttribute('data-u') || 'g|sa|dk|sn').split('|');
        var out = s.querySelector('[data-sk-cd]');
        if (out) out.textContent = Math.floor(left / 86400) + u[0] + ' ' + Math.floor(left % 86400 / 3600) + u[1] + ' ' + Math.floor(left % 3600 / 60) + u[2] + ' ' + (left % 60) + u[3];
      });
    }
    if (cds.length) { tick(); window.setInterval(tick, 1000); }

    // Reduced motion: the banner does not rotate on its own. The control then
    // truthfully starts in the PAUSED state - it offers to play, instead of
    // showing a pause button over a banner that is not moving.
    if (reduceMotion.matches) paused = true;
    paint();

    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        paused = !paused;
        if (paused) { stop(); } else { forceStart(); }
        paint();
      });
    }

    // Hold still while keyboard focus is on a MESSAGE (so a link is not pulled
    // out from under the user), and while the tab is hidden. Focus on the
    // pause control itself does not count - otherwise pressing play would
    // start a rotation that the button's own focus immediately stopped.
    promo.addEventListener('focusin', function (e) {
      if (pauseBtn && pauseBtn.contains(e.target)) return;
      stop();
    });
    promo.addEventListener('focusout', function (e) {
      if (pauseBtn && pauseBtn.contains(e.target)) return;
      if (!paused) forceStart();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else if (!paused) { forceStart(); }
    });
    reduceMotion.addEventListener('change', function () {
      if (reduceMotion.matches) { paused = true; stop(); }
      paint();
    });

    if (!paused) start();
  }

  /* ------------------------------------------------------------------------
     Mega menu, as the reference runs it today (vfr-sk-mega*.json, 11 Sep):
     - the pointer on a top-level link opens its panel at once — no intent
       delay; sliding to the next link cross-fades the two panels; leaving
       link and panel closes it at once. Both directions are the panel's
       200ms ease-out opacity + scale (sk-header.css).
     - the link still navigates when clicked.
     - keyboard: the toggle after each link opens its panel (Enter/Space) and
       Tab walks into it; Escape closes and returns to the toggle.
     ------------------------------------------------------------------------ */

  var megaItems = Array.prototype.slice.call(header.querySelectorAll('[data-sk-has-mega]'));
  var openItem = null;

  function closeMega(item) {
    if (!item) return;
    var panel = item.querySelector('[data-sk-mega]');
    var trigger = item.querySelector('[data-sk-mega-trigger]');
    if (panel) panel.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (openItem === item) openItem = null;
  }

  // The strip between the hovered link and the panel is kept inside the panel,
  // under that link only (see .sk-mega::before).
  function placeBridge(item, panel) {
    var hit = item.querySelector('.sk-nav__hit');
    var base = panel.offsetParent;
    if (!hit || !base) return;
    var h = hit.getBoundingClientRect();
    var b = base.getBoundingClientRect();
    panel.style.setProperty('--sk-bridge-x', Math.round(h.left - b.left) + 'px');
    panel.style.setProperty('--sk-bridge-w', Math.round(h.width) + 'px');
  }

  function openMega(item) {
    if (openItem && openItem !== item) closeMega(openItem);
    var panel = item.querySelector('[data-sk-mega]');
    var trigger = item.querySelector('[data-sk-mega-trigger]');
    if (panel) { placeBridge(item, panel); panel.classList.add('is-open'); }
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    openItem = item;
  }

  megaItems.forEach(function (item) {
    var trigger = item.querySelector('[data-sk-mega-trigger]');

    item.addEventListener('mouseenter', function () {
      if (!desktopQuery.matches) return;
      openMega(item);
    });

    item.addEventListener('mouseleave', function () {
      if (!desktopQuery.matches) return;
      closeMega(item);
    });

    // The toggle takes no pointer events, so its clicks come from Enter/Space.
    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (openItem === item) { closeMega(item); } else { openMega(item); }
      });
    }

    // Keep the panel open while focus is inside it, close when focus leaves.
    item.addEventListener('focusout', function (event) {
      if (!item.contains(event.relatedTarget)) closeMega(item);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openItem) {
      var trigger = openItem.querySelector('[data-sk-mega-trigger]');
      closeMega(openItem);
      if (trigger) trigger.focus();
    }
  });
})();

/* ==========================================================================
   Email signup panel (snippets/sk-signup.liquid). Opening from the promo
   button and after a post is Prestige's dialog engine; this only adds the
   OPTIONAL timed opening — off unless the owner sets a delay — once per
   browser, never on the bag / account / checkout pages and never over
   another open panel.
   ========================================================================== */
(function () {
  var panel = document.querySelector('[data-sk-signup]');
  if (!panel) return;
  var KEY = 'sk-signup-seen';
  function seen() { try { return window.localStorage.getItem(KEY) === '1'; } catch (e) { return true; } }
  function mark() { try { window.localStorage.setItem(KEY, '1'); } catch (e) {} }
  panel.addEventListener('dialog:after-hide', mark);

  var delay = parseInt(panel.getAttribute('data-delay'), 10) || 0;
  if (!delay || seen() || window.Shopify && window.Shopify.designMode) return;
  var path = window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
  if (/^\/(cart|account|checkout|challenge|password)(\/|$)/.test(path)) return;

  window.setTimeout(function () {
    if (seen() || panel.open || document.documentElement.classList.contains('lock')) return;
    mark();
    panel.show();
  }, delay * 1000);
})();

/* ==========================================================================
   Bag count — make the badge's VISIBILITY follow its own number.

   Prestige's <cart-count> only ever writes innerText (theme.js, `set
   itemCount`). The hidden attribute is decided once, by Liquid, at render
   time. So on a bag that was empty when the page loaded, adding the first
   item set the text to "1" and left `hidden` in place: measured on the draft
   at 390 — text "1", hidden true, display none, through +4200ms, and the
   badge only appeared after a reload. The customer added something and the
   bag icon said nothing.

   Liquid also caps the label at "99+", but a live update writes the raw
   count, so a hundredth item would print "100" into a 16px circle. The cap
   is re-applied here.

   Safe against the P0 family of feedback loops: this observes childList and
   characterData and writes the hidden ATTRIBUTE, which it does not observe.
   The one text write it does make ("99+") cannot re-trigger itself, because
   "99+" no longer matches the all-digits test that produced it.
   ========================================================================== */
(function () {
  var CAP = 99;
  var watched = null, mo = null;

  function apply(el) {
    var raw = (el.textContent || '').trim();
    var n = /^\d+$/.test(raw) ? parseInt(raw, 10) : (raw === CAP + '+' ? CAP + 1 : NaN);
    if (n > CAP && raw !== CAP + '+') { el.textContent = CAP + '+'; return; }   // terminates: "99+" is not all-digits
    var empty = !isNaN(n) ? n === 0 : raw === '';
    if (empty === !el.hasAttribute('hidden')) {                                  // conditional: never a same-value write
      if (empty) el.setAttribute('hidden', ''); else el.removeAttribute('hidden');
    }
  }

  function attach() {
    var el = document.querySelector('cart-count.sk-cart-count');
    if (!el || el === watched) { if (el) apply(el); return; }
    if (mo) mo.disconnect();
    watched = el;
    mo = new MutationObserver(function () { apply(el); });
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    apply(el);
  }

  attach();
  document.addEventListener('cart:change', attach);
  document.addEventListener('cart:refresh', function () { attach(); window.setTimeout(attach, 400); });
})();

/* ==========================================================================
   Bag count — answer the tap, not the round trip.

   Measured on the draft (390, real add): Shopify's own cart/add.js took 995ms,
   after which Prestige fetches cart.js again (+148ms). So the badge could not
   move for ~1.2s. The reference moves its bag in 167ms because it does not
   wait for the server at all (doc 17 rows 29/30).

   So the count is bumped the moment a real add-to-cart form is submitted, and
   the server's number then overwrites it — cart:change carries the authoritative
   item_count, and a failed add dispatches cart:refresh, which reconciles from
   /cart.js. The optimistic value is therefore never the last word; it only
   fills the gap the customer was staring at.

   Guarded: only a form that actually posts to /cart/add, only when its submit
   control is not disabled, and the quantity comes from the form.
   ========================================================================== */
(function () {
  function badge() { return document.querySelector('cart-count.sk-cart-count'); }
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM' || !/\/cart\/add/.test(f.getAttribute('action') || '')) return;
    if (e.defaultPrevented) return;
    var btn = f.querySelector('[type=submit]');
    if (btn && (btn.disabled || btn.getAttribute('aria-disabled') === 'true')) return;
    var el = badge();
    if (!el) return;
    var raw = (el.textContent || '').trim();
    var now = /^\d+$/.test(raw) ? parseInt(raw, 10) : (raw === '99+' ? 100 : 0);
    var q = f.querySelector('[name=quantity]');
    var add = q ? (parseInt(q.value, 10) || 1) : 1;
    el.textContent = String(now + add);      // the observer above unhides it and re-caps at 99+
  }, true);
})();
